import {
  GuidanceTourStep,
  GuidanceTourStepCompletionMap,
} from '../types';
import { getStepSubGroup, isFlatChecklistGroup } from './guidanceTourChecklistHierarchy';
import { isStepCompleted } from './tourStorage';

export type TourSectionSubCategory = {
  name: string;
  completedCount: number;
  totalCount: number;
  percent: number;
};

export type TourSectionStepEntry = {
  step: GuidanceTourStep;
  index: number;
  completed: boolean;
};

export type TourSectionSubCategoryGroup = {
  name: string;
  completedCount: number;
  totalCount: number;
  percent: number;
  stepEntries: TourSectionStepEntry[];
};

export type TourSectionProgress = {
  groupName: string;
  completedCount: number;
  totalCount: number;
  percent: number;
  weightPercent: number;
  firstStepIndex: number;
  firstIncompleteStepIndex: number;
  subCategories: TourSectionSubCategory[];
  subCategoryGroups: TourSectionSubCategoryGroup[];
  stepEntries: TourSectionStepEntry[];
  isActive: boolean;
  isFlatGroup: boolean;
};

type SectionAccumulator = {
  groupName: string;
  entries: Array<{
    step: GuidanceTourStep;
    index: number;
    completed: boolean;
  }>;
};

export function buildTourSectionProgress(
  steps: GuidanceTourStep[],
  stepCompleted: GuidanceTourStepCompletionMap,
  currentStepIndex: number,
): TourSectionProgress[] {
  if (steps.length === 0) {
    return [];
  }

  const groupOrder: string[] = [];
  const groupMap = new Map<string, SectionAccumulator>();

  steps.forEach((step, index) => {
    const groupName = step.group ?? 'General';
    let section = groupMap.get(groupName);
    if (!section) {
      section = { groupName, entries: [] };
      groupMap.set(groupName, section);
      groupOrder.push(groupName);
    }

    section.entries.push({
      step,
      index,
      completed: isStepCompleted(stepCompleted, step.id),
    });
  });

  const totalSteps = steps.length;

  return groupOrder.map(groupName => {
    const { entries } = groupMap.get(groupName)!;
    const completedCount = entries.filter(entry => entry.completed).length;
    const totalCount = entries.length;
    const percent = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;

    const subCategoryOrder: string[] = [];
    const subCategoryEntriesMap = new Map<string, TourSectionStepEntry[]>();

    entries.forEach(({ step, index, completed }) => {
      const subName = getStepSubGroup(step);
      if (!subCategoryEntriesMap.has(subName)) {
        subCategoryEntriesMap.set(subName, []);
        subCategoryOrder.push(subName);
      }

      subCategoryEntriesMap.get(subName)!.push({ step, index, completed });
    });

    const subCategoryGroups: TourSectionSubCategoryGroup[] = subCategoryOrder.map(
      name => {
        const stepEntries = subCategoryEntriesMap.get(name) ?? [];
        const subCompletedCount = stepEntries.filter(entry => entry.completed).length;
        const subTotalCount = stepEntries.length;

        return {
          name,
          completedCount: subCompletedCount,
          totalCount: subTotalCount,
          percent:
            subTotalCount > 0
              ? Math.round((subCompletedCount / subTotalCount) * 100)
              : 0,
          stepEntries,
        };
      },
    );

    const subCategories: TourSectionSubCategory[] = subCategoryGroups.map(
      ({ name, completedCount: subCompletedCount, totalCount: subTotalCount, percent: subPercent }) => ({
        name,
        completedCount: subCompletedCount,
        totalCount: subTotalCount,
        percent: subPercent,
      }),
    );

    const firstIncompleteEntry =
      entries.find(entry => !entry.completed) ?? entries[0];

    return {
      groupName,
      completedCount,
      totalCount,
      percent,
      weightPercent: (totalCount / totalSteps) * 100,
      firstStepIndex: entries[0]?.index ?? 0,
      firstIncompleteStepIndex: firstIncompleteEntry?.index ?? 0,
      subCategories,
      subCategoryGroups,
      stepEntries: entries.map(({ step, index, completed }) => ({
        step,
        index,
        completed,
      })),
      isActive: entries.some(entry => entry.index === currentStepIndex),
      isFlatGroup: isFlatChecklistGroup(groupName),
    };
  });
}
